import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ViewportService } from '@core/services/viewport.service';
import { ECarouselComponent, ECarouselLabels, ECarouselSlideDirective } from '@shared/components/content/e-carousel/e-carousel.component';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdIChingHexagramFigureComponent } from '../figure/hd-iching-hexagram-figure.component';
import { toggledInSet } from '../core/iching-set.util';
import { ICHING_TRIGRAMS } from '../core/iching-trigrams.const';
import {
  IChingHexagram, IChingHexagramCommentary, IChingIndexEntry, IChingLinePosition, IChingMethodTerm, IChingTrigramId,
} from '../core/iching.models';
import { HdIChingProseComponent } from './hd-iching-prose.component';
import { HdIChingCommentaryComponent } from './hd-iching-commentary.component';
import { HdIChingRelationsComponent } from './hd-iching-relations.component';
import { IChingTextSegment, buildIChingTermMatcher, segmentIChingTerms, tokenizeIChingWords } from './iching-terms.util';

/** Naam en beeld van een trigram uit `general.json` van de bron. */
export interface IChingTrigramName {
  readonly name: string;
  readonly image: string;
}

/** Sleutel waarop de handmatige reader-state (lijnen, uitleg) reset. */
interface ReaderResetKey {
  readonly hexagram: number;
  readonly focusLine: IChingLinePosition | undefined;
  readonly openLines: readonly IChingLinePosition[] | undefined;
}

/**
 * Leesweergave van één hexagram: kop (figuur, nummer, naam, titel, trigrammen,
 * kerntekens, heren, bronbadge), kern (duiding, Oordeel, Beeld, lijnen), uitleg
 * inklapbaar per blok en Boek III op verzoek. Handmatig open/dicht geklapte
 * lijnen en uitleg resetten bij een ander hexagram of een andere context. Gebruikt door het paneel (poortcontext, `focusLine`) en
 * door de raadpleegpagina (`openLines` = bewegende lijnen).
 */
@Component({
  selector: 'hd-iching-hexagram-reader',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    ECarouselComponent,
    ECarouselSlideDirective,
    HdIChingHexagramFigureComponent,
    HdIChingProseComponent,
    HdIChingCommentaryComponent,
    HdIChingRelationsComponent,
  ],
  templateUrl: './hd-iching-hexagram-reader.component.html',
  styleUrl: './hd-iching-hexagram-reader.component.scss',
})
export class HdIChingHexagramReaderComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly viewport = inject(ViewportService);

  readonly hexagram = input.required<IChingHexagram>();
  /** Poortcontext: deze lijn open, de andere dicht. */
  readonly focusLine = input<IChingLinePosition | undefined>(undefined);
  /** Pagina-context: alleen deze lijnen open (bewegende lijnen). Wint van focusLine. */
  readonly openLines = input<readonly IChingLinePosition[] | undefined>(undefined);
  readonly commentary = input<IChingHexagramCommentary | undefined>(undefined);
  readonly commentaryBusy = input(false);
  /** Introductie en uitleg verbergen (pagina: pas na je eigen duiding). */
  readonly hideExplanations = input(false);
  /** Naam en beeld per trigram (host laadt `general.json` van de bron); zonder: alleen symbool en pinyin. */
  readonly trigramNames = input<Readonly<Record<IChingTrigramId, IChingTrigramName>> | undefined>(undefined);
  /** Woorden in het Oordeel aanklikbaar (pagina: trefwoorden aanstrepen). */
  readonly markable = input(false);
  /** Aangestreepte woorden (genormaliseerd, zie `cleanIChingWord`); vergelijking hoofdletterongevoelig. */
  readonly highlightWords = input<readonly string[]>([]);
  /** Naam en titel verborgen (pagina: pas na "toon naam" in de situatie); een knop vraagt ze op. */
  readonly hideTitle = input(false);
  /**
   * Begrippen uit de methode (pagina): als heel woord in Oordeel, Beeld en de lijnen gemarkeerd
   * met een stippellijn en de korte uitleg als tooltip.
   */
  readonly terms = input<readonly IChingMethodTerm[]>([]);
  /** Index van de bron (titels van de verwante hexagrammen); zonder index geen titels. */
  readonly relationEntries = input<readonly IChingIndexEntry[] | undefined>(undefined);
  /** Pagina-context: geen sectie verwante hexagrammen (de gids werkt met de kern in C3). */
  readonly hideRelations = input(false);
  /**
   * Knop "Poort N in Human Design" in de kop tonen: alleen als de host de
   * poort-info kan openen (paneel met een actieve chart-pagina).
   */
  readonly hdGateInfoAvailable = input(false);
  readonly commentaryRequested = output<void>();
  /** Klik op een verwant hexagram (tegenhanger, omgekeerd, gewisseld, kern, paleislid). */
  readonly relatedSelected = output<number>();
  /** Klik op "toon naam" in de kop terwijl `hideTitle` aan staat. */
  readonly titleRevealRequested = output<void>();
  /** Aangeklikt woord uit het Oordeel, genormaliseerd (randleestekens weg, kleine letters). */
  readonly wordClicked = output<string>();
  /** Klik op "Poort N in Human Design": het poortnummer (= hexagramnummer); de host opent de poort-info. */
  readonly hdGateInfoRequested = output<number>();

  /** Nieuw hexagram of nieuwe context: handmatige toggles vervallen. */
  private readonly resetKey = computed<ReaderResetKey>(
    () => ({ hexagram: this.hexagram().number, focusLine: this.focusLine(), openLines: this.openLines() }),
    { equal: sameResetKey },
  );

  protected readonly explainOpen = linkedSignal<ReaderResetKey, ReadonlySet<string>>({
    source: this.resetKey,
    computation: () => new Set<string>(),
  });
  /** Handmatig open/dicht geklapte lijnen, per positie; wint van de context. */
  protected readonly lineOverrides = linkedSignal<ReaderResetKey, ReadonlyMap<IChingLinePosition, boolean>>({
    source: this.resetKey,
    computation: () => new Map<IChingLinePosition, boolean>(),
  });

  /** Kopregels voor trigrammen en kerntekens: symbool, pinyin en (indien bekend) naam en beeld. */
  protected readonly trigramLabels = computed(() => {
    const hexagram = this.hexagram();
    const names = this.trigramNames();
    return {
      upper: trigramLabel(hexagram.trigrams.upper, names),
      lower: trigramLabel(hexagram.trigrams.lower, names),
      nuclearUpper: trigramLabel(hexagram.nuclearTrigrams.upper, names),
      nuclearLower: trigramLabel(hexagram.nuclearTrigrams.lower, names),
    };
  });

  private readonly termMatcher = computed(() => buildIChingTermMatcher(this.terms()));

  /** Verzen van het Oordeel als woorden (met hun begrip), voor de aanklikbare weergave. */
  protected readonly judgementWords = computed(() => {
    const matcher = this.termMatcher();
    return this.hexagram().text.judgement.text.map(verse => tokenizeIChingWords(verse, matcher));
  });

  /** Verzen in stukken met de begrippen gemarkeerd; eenmaal per hexagram en begrippenlijst. */
  protected readonly judgementSegments = computed(() => this.segmentVerses(this.hexagram().text.judgement.text));
  protected readonly imageSegments = computed(() => this.segmentVerses(this.hexagram().text.image.text));
  protected readonly lineSegments = computed(() => this.hexagram().text.lines.map(line => this.segmentVerses(line.text)));

  private readonly highlightSet = computed(() => new Set(this.highlightWords().map(word => word.toLowerCase())));

  /** Phone-breedte: de zes lijnen als carrousel (één lijn per slide) in plaats van een gestapelde lijst. */
  protected readonly linesAsCarousel = computed(() => this.viewport.isPhone());

  /** Startslide van de carrousel: de focus-lijn, anders de eerste open lijn, anders lijn 1; reset per hexagram/context. */
  protected readonly carouselIndex = linkedSignal<ReaderResetKey, number>({
    source: this.resetKey,
    computation: key => Math.max(0, (key.focusLine ?? key.openLines?.[0] ?? 1) - 1),
  });

  protected readonly carouselLabels = computed<ECarouselLabels>(() => ({
    previous: this.t('humanDesign.iching.carousel.previous'),
    next: this.t('humanDesign.iching.carousel.next'),
    slide: this.t('humanDesign.iching.carousel.slide'),
  }));

  protected readonly lineOpen = computed(() => {
    const open = this.openLines();
    const focus = this.focusLine();
    const overrides = this.lineOverrides();
    return (position: IChingLinePosition): boolean =>
      overrides.get(position) ?? (open ? open.includes(position) : focus === undefined || focus === position);
  });

  protected t(key: string, params?: Record<string, unknown>): string {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, params, this.contentLang.effectiveLang());
  }

  protected isMarked(word: string): boolean {
    const cleaned = cleanIChingWord(word);
    return cleaned.length > 0 && this.highlightSet().has(cleaned);
  }

  protected clickWord(word: string): void {
    const cleaned = cleanIChingWord(word);
    if (cleaned.length > 0) {
      this.wordClicked.emit(cleaned);
    }
  }

  private segmentVerses(verses: readonly string[]): IChingTextSegment[][] {
    const matcher = this.termMatcher();
    return verses.map(verse => segmentIChingTerms(verse, matcher));
  }

  protected toggleExplain(id: string): void {
    this.explainOpen.update(set => toggledInSet(set, id));
  }

  protected toggleLine(position: IChingLinePosition): void {
    const isOpen = this.lineOpen()(position);
    this.lineOverrides.update(map => new Map(map).set(position, !isOpen));
  }
}

/** Woord zonder leestekens aan de randen, in kleine letters ("Welslagen." → "welslagen"). */
export function cleanIChingWord(word: string): string {
  return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLowerCase();
}

function sameResetKey(a: ReaderResetKey, b: ReaderResetKey): boolean {
  if (a.hexagram !== b.hexagram || a.focusLine !== b.focusLine) {
    return false;
  }
  const left = a.openLines;
  const right = b.openLines;
  if (left === right) {
    return true;
  }
  if (left === undefined || right === undefined || left.length !== right.length) {
    return false;
  }
  return left.every((line, i) => line === right[i]);
}

interface IChingTrigramLabel {
  readonly short: string;
  readonly full: string;
}

/**
 * `short` = symbool + naam (of pinyin), `full` = symbool, pinyin, naam en beeld.
 * Legge kent geen naam (host valt terug op de Chinese naam): die wordt niet
 * herhaald als ze op de pinyin na accenten gelijk is.
 */
function trigramLabel(
  id: IChingTrigramId,
  names: Readonly<Record<IChingTrigramId, IChingTrigramName>> | undefined,
): IChingTrigramLabel {
  const facts = ICHING_TRIGRAMS[id];
  const entry = names?.[id];
  const name = entry && plain(entry.name) !== plain(facts.pinyin) ? entry.name : undefined;
  const image = entry?.image ? ` (${entry.image})` : '';
  return {
    short: `${facts.symbol} ${name ?? facts.pinyin}`,
    full: `${facts.symbol} ${facts.pinyin}${name ? `, ${name}` : ''}${image}`,
  };
}

function plain(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}
